import React, { useMemo } from 'react';
import { ExpenseCategory, TaxExpense } from '../../../types/database';
import { EXPENSE_CATEGORIES } from '../../../lib/expense-categories';
import { formatCurrency } from '../../../lib/format';
import { cn } from '../../../lib/utils';

interface ExpensesByCategoryProps {
  expenses: TaxExpense[];
  selectedCategory: ExpenseCategory | null;
  onSelectCategory: (category: ExpenseCategory | null) => void;
}

/** Ranked category totals. Click a row to filter the list to that category. */
export const ExpensesByCategory: React.FC<ExpensesByCategoryProps> = ({ expenses, selectedCategory, onSelectCategory }) => {
  const rows = useMemo(() => {
    const total = expenses.reduce((sum, e) => sum + e.amount, 0);
    return EXPENSE_CATEGORIES.map((category) => {
      const amount = expenses.filter((e) => e.category === category.id).reduce((sum, e) => sum + e.amount, 0);
      return { ...category, amount, share: total > 0 ? amount / total : 0 };
    })
      .filter((row) => row.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [expenses]);

  if (rows.length === 0) {
    return <p className="py-6 text-center text-[13px] text-fg-subtle">Categories will appear once you add expenses.</p>;
  }

  const largest = rows[0].amount;

  return (
    <ul className="flex flex-col gap-1">
      {rows.map((row) => {
        const selected = selectedCategory === row.id;
        const dimmed = selectedCategory !== null && !selected;
        return (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onSelectCategory(selected ? null : row.id)}
              aria-pressed={selected}
              className={cn(
                'w-full rounded-lg px-2 py-1.5 text-left transition-[background-color,opacity] duration-150 hover:bg-white/[0.04]',
                selected && 'bg-white/[0.06]',
                dimmed && 'opacity-45'
              )}
            >
              <span className="flex items-center gap-2 text-[13px]">
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
                <span className="min-w-0 flex-1 truncate text-fg-muted">{row.label}</span>
                <span className="numeric text-fg">{formatCurrency(row.amount)}</span>
                <span className="numeric w-9 text-right text-xs text-fg-subtle">{Math.round(row.share * 100)}%</span>
              </span>
              <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-white/[0.05]">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${(row.amount / largest) * 100}%`, backgroundColor: row.color }}
                />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
